import {
    useContext,
    type FC,
    useRef
} from "react";
import type IBarChartProps from "./type";
import type {
    BarChartSnapshot,
    BarChartLayout,
    BarChartScale,
    BarChartBar
} from "./type";
import stylesheet, {
    getBarChartMeasures,
    useStyles
} from "./stylesheet";
import BarChartSignLegend from "./components/signLegend";
import BarChartSignTooltip from "./components/signTooltip";
import {
    getBarChartLayout
} from "./util";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../core/hooks";
import Chart, {
    useChartValueTransition,
    getChartStaggerProgress,
    formatChartCompact,
    getChartBandIndex,
    getChartNiceScale,
    getChartTextWidth,
    CHART_TICK_COUNT,
    getChartBarPath,
    useChartSeries,
    useChartModel
} from "../chart";
import type {
    ChartNumericColorIndex,
    ChartTooltipContent,
    ChartLegendItem,
    ChartPlotLayout,
    ChartTooltipRow
} from "../chart/type";
import {
    Text as SvgText,
    Path,
    Rect,
    G
} from "react-native-svg";
import {
    CHART_PATTERN_ANGLES
} from "../chart/util";
import {
    formatNumber
} from "../../helpers/locale";
import {
    ChartContext
} from "../chart/context";
import ChartPatternDefs from "../chart/components/pattern";

const BAR_CHART_EXPANDED_TOTAL = 100;

const BAR_CHART_EXPANDED_TICKS = [
    0,
    25,
    50,
    75,
    100
];

const BarChart: FC<IBarChartProps> = ({
    customLocalize: customLocalizeProp,
    labelBehaviour: labelBehaviourProp,
    valueFormatter: valueFormatterProp,
    renderTooltip: renderTooltipProp,
    customTheme: customThemeProp,
    seriesKeys: seriesKeysProp,
    stackBehaviour = "grouped",
    colorBehaviour = "series",
    orientation = "vertical",
    plotSize = "medium",
    categoryFormatter,
    isAnimatedOnMount,
    tooltipBehaviour,
    onActiveChange,
    categoryTitle,
    highlightKey,
    activeIndex,
    categoryKey,
    summary,
    style,
    data
}) => {
    const context = useContext(ChartContext);

    const customTheme = customThemeProp ?? context?.customTheme;

    const customLocalize = customLocalizeProp ?? context?.customLocalize;

    const {
        shapeConfigs,
        typography,
        durations,
        radiuses,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        activeLocale,
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const isHorizontal = orientation === "horizontal";

    const isExpanded = stackBehaviour === "expanded";

    const isStacked = stackBehaviour !== "grouped";

    const isDiverging = colorBehaviour === "diverging";

    const labelBehaviour = labelBehaviourProp ?? (isHorizontal ? "all" : "none");

    const seriesKeys = seriesKeysProp ?? (context ? Object.keys(context.config) : Object.keys(data[0] ?? {}).filter((key) => key !== categoryKey));

    const isCustomFormatter = !!valueFormatterProp;

    const valueFormatter = valueFormatterProp ?? ((value: number) => formatNumber({
        options: {
            maximumFractionDigits: 2
        },
        locale: activeLocale,
        value
    }));

    const isHighlighted = !isDiverging && !!highlightKey && seriesKeys.includes(highlightKey);

    const highlightLegendItems: Array<ChartLegendItem> | undefined = isHighlighted ? seriesKeys.map((key, index) => ({
        colorIndex: key === highlightKey ? context?.config[key]?.colorIndex ?? ((index % 8) + 1) as ChartNumericColorIndex : "muted",
        title: context?.config[key]?.title ?? key,
        key
    })) : undefined;

    useChartModel({
        model: {
            legendItems: isDiverging ? [] : highlightLegendItems,
            isShowTotal: isStacked,
            categoryFormatter,
            valueFormatter,
            categoryTitle,
            categoryKey,
            kind: "bar",
            seriesKeys,
            isStacked,
            data
        }
    });

    const {
        patternID,
        series
    } = useChartSeries({
        seriesKeys
    });

    const {
        negative: negativeColor,
        positive: positiveColor,
        hover: hoverColor,
        muted: mutedColor,
        high: highColor,
        mid: midColor,
        low: lowColor
    } = useStyles({
        colors
    });

    const measures = getBarChartMeasures({
        shapeConfigs,
        typography,
        radiuses,
        spaces
    });

    const activeSeries = series.filter((item) => !item.isHidden);

    const getCategoryText = (index: number): string => {
        const value = data[index]?.[categoryKey] ?? index;

        return categoryFormatter ? categoryFormatter(value, index) : String(value);
    };

    const categories = data.map((_, index) => getCategoryText(index));

    const realValues = data.map((row) => series.map((item) => item.isHidden ? 0 : Number(row[item.key]) || 0));

    const targetValues = isExpanded ? realValues.map((items) => {
        const positives = items.map((item) => Math.max(0, item));

        const total = positives.reduce((sum, item) => sum + item, 0);

        return positives.map((item) => total ? item / total * BAR_CHART_EXPANDED_TOTAL : 0);
    }) : realValues;

    const getScale = (): BarChartScale => {
        if(isExpanded) {
            return {
                labels: BAR_CHART_EXPANDED_TICKS.map((tick) => formatNumber({
                    value: tick / BAR_CHART_EXPANDED_TOTAL,
                    options: {
                        maximumFractionDigits: 0,
                        style: "percent"
                    },
                    locale: activeLocale
                })),
                ticks: BAR_CHART_EXPANDED_TICKS,
                max: BAR_CHART_EXPANDED_TOTAL,
                min: 0
            };
        }

        const extents = targetValues.map((items) => ({
            negative: items.filter((item) => item < 0).reduce((total, item) => total + item, 0),
            positive: items.filter((item) => item > 0).reduce((total, item) => total + item, 0)
        }));

        const niceScale = getChartNiceScale({
            max: isStacked ? Math.max(0, ...extents.map((item) => item.positive)) : Math.max(0, ...targetValues.flat()),
            min: isStacked ? Math.min(0, ...extents.map((item) => item.negative)) : Math.min(0, ...targetValues.flat()),
            count: CHART_TICK_COUNT
        });

        return {
            ...niceScale,
            labels: niceScale.ticks.map((tick) => isCustomFormatter ? valueFormatter(tick) : formatChartCompact(tick, activeLocale))
        };
    };

    const scale = getScale();

    const barTexts = isStacked ? realValues.map((items) => valueFormatter(items.reduce((sum, item) => sum + item, 0))) : realValues.flat().map((item) => valueFormatter(item));

    const valueLabelWidth = Math.max(0, ...barTexts.map((text) => getChartTextWidth({
        fontSize: measures.typography.fontSize,
        text
    })));

    const shapeKey = [
        stackBehaviour,
        orientation,
        seriesKeys.join(","),
        categories.join(",")
    ].join("|");

    const flatValues = [
        scale.min,
        scale.max,
        ...targetValues.flat()
    ];

    const transition = useChartValueTransition({
        values: flatValues,
        customTheme,
        shapeKey
    });

    const snapshotRef = useRef<BarChartSnapshot>({
        valueLabelWidth,
        stackBehaviour,
        orientation,
        categories,
        seriesKeys,
        shapeKey,
        scale
    });

    const previousSnapshotRef = useRef<BarChartSnapshot | undefined>(undefined);

    if(snapshotRef.current.shapeKey !== shapeKey) {
        previousSnapshotRef.current = snapshotRef.current;
    }

    snapshotRef.current = {
        valueLabelWidth,
        stackBehaviour,
        orientation,
        categories,
        seriesKeys,
        shapeKey,
        scale
    };

    const displayScale: BarChartScale = {
        ...scale,
        max: transition.values[1] ?? scale.max,
        min: transition.values[0] ?? scale.min
    };

    const isSettled = !transition.previous && transition.values.length === flatValues.length && transition.values.every((value, index) => value === flatValues[index]);

    const getMatrix = (values: Array<number>, count: number, seriesCount: number): Array<Array<number>> => {
        return Array.from({
            length: count
        }, (_, categoryIndex) => Array.from({
            length: seriesCount
        }, (_, seriesIndex) => values[2 + categoryIndex * seriesCount + seriesIndex] ?? 0));
    };

    const getLayout = ({
        height,
        isRTL,
        width
    }: Pick<ChartPlotLayout, "height" | "isRTL" | "width">): BarChartLayout => getBarChartLayout({
        values: getMatrix(transition.values, categories.length, series.length),
        weights: series.map((item) => item.opacity),
        scale: displayScale,
        valueLabelWidth,
        stackBehaviour,
        labelBehaviour,
        valueFormatter,
        orientation,
        categories,
        measures,
        height,
        isRTL,
        width
    });

    const getPreviousLayout = ({
        height,
        isRTL,
        width
    }: Pick<ChartPlotLayout, "height" | "isRTL" | "width">): BarChartLayout | undefined => {
        const snapshot = previousSnapshotRef.current;

        if(!snapshot || !transition.previous) {
            return undefined;
        }

        return getBarChartLayout({
            values: getMatrix(transition.previous.values, snapshot.categories.length, snapshot.seriesKeys.length),
            scale: {
                ...snapshot.scale,
                max: transition.previous.values[1] ?? snapshot.scale.max,
                min: transition.previous.values[0] ?? snapshot.scale.min
            },
            weights: snapshot.seriesKeys.map(() => 1),
            valueLabelWidth: snapshot.valueLabelWidth,
            stackBehaviour: snapshot.stackBehaviour,
            orientation: snapshot.orientation,
            categories: snapshot.categories,
            labelBehaviour: "none",
            valueFormatter,
            measures,
            height,
            isRTL,
            width
        });
    };

    const getTooltipRows = (index: number): Array<ChartTooltipRow> => {
        const rows = activeSeries.map((item): ChartTooltipRow => {
            const value = Number(data[index]?.[item.key]) || 0;

            const sign = value < 0 ? "negative" : "positive";

            return {
                title: isDiverging && activeSeries.length === 1 ? localize(`chart-${sign}`) : item.title,
                key: isDiverging ? `${sign}:${item.key}` : item.key,
                value: valueFormatter(value),
                colorIndex: item.colorIndex,
                shape: "square"
            };
        });

        return isStacked ? rows.reverse() : rows;
    };

    const getTooltip = (index: number): ChartTooltipContent => {
        const total = activeSeries.reduce((sum, item) => sum + (Number(data[index]?.[item.key]) || 0), 0);

        return {
            sum: isStacked ? {
                title: localize("chart-total"),
                value: valueFormatter(total)
            } : undefined,
            title: getCategoryText(index),
            rows: getTooltipRows(index)
        };
    };

    const getReading = (index: number): string => {
        const rows = getTooltipRows(index).map((row) => `${row.title} ${row.value}`).join(", ");

        return `${getCategoryText(index)}: ${rows}`;
    };

    const getHighest = (): string => {
        const primary = data.map((_, index) => {
            const items = activeSeries.map((item) => Number(data[index]?.[item.key]) || 0);

            return isStacked ? items.reduce((sum, item) => sum + item, 0) : Math.max(0, ...items);
        });

        const highest = Math.max(...primary);

        const index = primary.indexOf(highest);

        if(index < 0) {
            return "";
        }

        return localize("chart-highest-value", [
            getCategoryText(index),
            valueFormatter(highest)
        ]);
    };

    const currentSummary = summary ?? [
        context?.title,
        context?.description,
        getHighest()
    ].filter(Boolean).join(". ");

    const getIndex: Parameters<typeof Chart.Plot>[0]["getIndex"] = (point, layout) => {
        if(point.y < 0 || point.y > layout.height) {
            return undefined;
        }

        const geometry = getLayout(layout);

        return isHorizontal ? getChartBandIndex({
            size: geometry.plot.height,
            start: geometry.plot.y,
            count: data.length,
            position: point.y
        }) : getChartBandIndex({
            size: geometry.plot.width,
            start: geometry.plot.x,
            isRTL: layout.isRTL,
            count: data.length,
            position: point.x
        });
    };

    const getAnchor: Parameters<typeof Chart.Plot>[0]["getAnchor"] = (index, _seriesIndex, layout) => {
        const geometry = getLayout(layout);

        const band = geometry.bands[index];

        const bars = geometry.bars.filter((bar) => bar.categoryIndex === index);

        if(!band) {
            return undefined;
        }

        if(isHorizontal) {
            return {
                x: layout.isRTL ? Math.min(geometry.baseline, ...bars.map((bar) => bar.x)) : Math.max(geometry.baseline, ...bars.map((bar) => bar.x + bar.width)),
                xStart: geometry.baseline,
                y: band.center
            };
        }

        return {
            xStart: layout.isRTL ? Math.max(band.center, ...bars.map((bar) => bar.x + bar.width)) : Math.min(band.center, ...bars.map((bar) => bar.x)),
            x: layout.isRTL ? Math.min(band.center, ...bars.map((bar) => bar.x)) : Math.max(band.center, ...bars.map((bar) => bar.x + bar.width)),
            y: geometry.plot.y
        };
    };

    const getBarColor = (bar: BarChartBar, keys: Array<string>): string => {
        if(isDiverging) {
            return bar.value < 0 ? negativeColor : positiveColor;
        }

        const key = keys[bar.seriesIndex];

        const item = series.find((current) => current.key === key);

        if(!item) {
            return mutedColor;
        }

        if(highlightKey && keys.includes(highlightKey) && key !== highlightKey) {
            return mutedColor;
        }

        return item.color;
    };

    const renderBar = ({
        isHorizontal: isLayerHorizontal,
        isCurrent,
        geometry,
        layout,
        keys,
        bar
    }: {
        geometry: BarChartLayout;
        layout: ChartPlotLayout;
        isHorizontal: boolean;
        keys: Array<string>;
        isCurrent: boolean;
        bar: BarChartBar;
    }) => {
        const key = keys[bar.seriesIndex] ?? "";

        const item = series.find((current) => current.key === key);

        const order = !isLayerHorizontal && layout.isRTL ? data.length - 1 - bar.categoryIndex : bar.categoryIndex;

        const progress = isCurrent ? getChartStaggerProgress({
            progress: layout.entrance.progress,
            stagger: durations.stagger,
            duration: durations.long,
            count: data.length,
            index: order
        }) : 1;

        const path = getChartBarPath({
            x: isLayerHorizontal ? geometry.baseline + (bar.x - geometry.baseline) * progress : bar.x,
            y: isLayerHorizontal ? bar.y : geometry.baseline + (bar.y - geometry.baseline) * progress,
            height: isLayerHorizontal ? bar.height : bar.height * progress,
            width: isLayerHorizontal ? bar.width * progress : bar.width,
            radius: measures.radius,
            side: bar.side
        });

        const color = getBarColor(bar, keys);

        const isPatterned = isCurrent && !isDiverging && !!item && color === item.color;

        return <G
            opacity={isCurrent ? item?.opacity ?? 1 : 1}
            key={`${bar.categoryIndex}-${key}`}
        >
            <Path
                fill={color}
                d={path}
            />
            {
                isPatterned && item ? CHART_PATTERN_ANGLES[item.pattern].map((angle) => <Path
                    fill={`url(#${patternID}-${angle})`}
                    key={angle}
                    d={path}
                />) : null
            }
        </G>;
    };

    const renderLayer = ({
        isHorizontal: isLayerHorizontal,
        scale: layerScale,
        isCurrent,
        geometry,
        opacity,
        layout,
        keys
    }: {
        geometry: BarChartLayout;
        layout: ChartPlotLayout;
        isHorizontal: boolean;
        scale: BarChartScale;
        keys: Array<string>;
        isCurrent: boolean;
        opacity: number;
    }) => {
        const {
            plot
        } = geometry;

        const activeBand = isCurrent && layout.activeIndex !== undefined ? geometry.bands[layout.activeIndex] : undefined;

        const labelStyle = measures.typography;

        const isLabelVisible = isCurrent && isSettled && layout.entrance.progress >= 1;

        const getHighlight = () => {
            if(!activeBand) {
                return null;
            }

            if(!isLayerHorizontal) {
                return <Rect
                    width={activeBand.size}
                    height={plot.height}
                    x={activeBand.start}
                    rx={measures.radius}
                    fill={hoverColor}
                    y={plot.y}
                />;
            }

            const start = layout.isRTL ? 0 : plot.x - measures.spacingSm;

            const end = layout.isRTL ? plot.x + plot.width + measures.spacingSm : layout.width;

            return <Rect
                height={activeBand.size}
                y={activeBand.start}
                rx={measures.radius}
                width={end - start}
                fill={hoverColor}
                x={start}
            />;
        };

        const renderAxes = () => {
            if(isLayerHorizontal) {
                return geometry.categoryLabels.map((label) => <SvgText
                    fill={isCurrent && layout.activeIndex === label.categoryIndex ? highColor : lowColor}
                    key={`category-${label.categoryIndex}`}
                    fontFamily={labelStyle.fontFamily}
                    fontWeight={labelStyle.fontWeight}
                    fontSize={labelStyle.fontSize}
                    textAnchor={label.anchor}
                    aria-hidden={true}
                    x={label.x}
                    y={label.y}
                >
                    {label.text}
                </SvgText>);
            }

            return <>
                <Chart.Axis
                    ticks={geometry.valueTicks}
                    customTheme={customTheme}
                    orientation="vertical"
                    isRTL={layout.isRTL}
                    plot={plot}
                />
                <Chart.Axis
                    ticks={geometry.categoryTicks}
                    customTheme={customTheme}
                    orientation="horizontal"
                    plot={plot}
                />
            </>;
        };

        return <G
            key={isCurrent ? "current" : "previous"}
            opacity={opacity}
        >
            <Chart.Grid
                baseline={layerScale.min < 0 ? geometry.baseline : undefined}
                orientation={isLayerHorizontal ? "vertical" : "horizontal"}
                positions={geometry.gridPositions}
                customTheme={customTheme}
                plot={plot}
            />
            {getHighlight()}
            {geometry.bars.map((bar) => renderBar({
                isHorizontal: isLayerHorizontal,
                isCurrent,
                geometry,
                layout,
                keys,
                bar
            }))}
            {renderAxes()}
            {
                isLabelVisible ? geometry.labels.map((label) => <SvgText
                    fill={layout.activeIndex === label.categoryIndex ? highColor : midColor}
                    key={`${label.categoryIndex}-${label.x}-${label.y}`}
                    fontFamily={labelStyle.fontFamily}
                    fontWeight={measures.labelWeight}
                    fontSize={labelStyle.fontSize}
                    textAnchor={label.anchor}
                    aria-hidden={true}
                    x={label.x}
                    y={label.y}
                >
                    {label.text}
                </SvgText>) : null
            }
        </G>;
    };

    const renderPlot = (layout: ChartPlotLayout) => {
        const geometry = getLayout(layout);

        const previousGeometry = getPreviousLayout(layout);

        const previousSnapshot = previousSnapshotRef.current;

        return <>
            <ChartPatternDefs
                patterns={series.map((item) => item.pattern)}
                customTheme={customTheme}
                id={patternID}
            />
            {
                previousGeometry && previousSnapshot && transition.previous ? renderLayer({
                    opacity: transition.previous.opacity * layout.entrance.opacity,
                    isHorizontal: previousSnapshot.orientation === "horizontal",
                    keys: previousSnapshot.seriesKeys,
                    scale: previousSnapshot.scale,
                    geometry: previousGeometry,
                    isCurrent: false,
                    layout
                }) : null
            }
            {renderLayer({
                opacity: transition.opacity * layout.entrance.opacity,
                scale: displayScale,
                keys: seriesKeys,
                isCurrent: true,
                isHorizontal,
                geometry,
                layout
            })}
        </>;
    };

    const renderSignTooltip = (content: ChartTooltipContent) => <BarChartSignTooltip
        customTheme={customTheme}
        content={content}
    />;

    const plot = <Chart.Plot
        renderTooltip={renderTooltipProp ?? (isDiverging ? renderSignTooltip : undefined)}
        isAnimatedOnMount={isAnimatedOnMount}
        tooltipBehaviour={tooltipBehaviour}
        seriesCount={activeSeries.length}
        onActiveChange={onActiveChange}
        customLocalize={customLocalize}
        customTheme={customTheme}
        activeIndex={activeIndex}
        style={[
            style,
            stylesheet.container
        ]}
        summary={currentSummary}
        pointCount={data.length}
        getTooltip={getTooltip}
        getReading={getReading}
        getAnchor={getAnchor}
        plotSize={plotSize}
        getIndex={getIndex}
    >
        {renderPlot}
    </Chart.Plot>;

    if(!isDiverging) {
        return plot;
    }

    return <>
        {plot}
        <BarChartSignLegend
            customLocalize={customLocalize}
            customTheme={customTheme}
        />
    </>;
};
export default BarChart;
